import React, { useEffect, useRef, useState, useMemo } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, Camera, CheckCircle2, XCircle, Search, Power, PowerOff, Users, Keyboard, RefreshCw, SwitchCamera, Smartphone, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import QRCode from "qrcode";

/**
 * QR Check-in scanner.
 *
 * Supports THREE input methods:
 *   1) Live camera (Html5Qrcode) — phone/laptop webcam.
 *   2) Manual paste — type/paste the QR token + click Verify.
 *   3) **Hardware wired/wireless barcode scanner** (keyboard-emulation).
 *      Most scanners just "type" the decoded text very fast (< 50ms
 *      between keystrokes) and end with Enter. We capture that by
 *      keeping a hidden input always focused, AND by detecting any
 *      rapid keystroke burst anywhere on the page.
 *
 * On every scan we show a modal popup:
 *   • Green tick + full delegate details + "OK" button on success
 *   • Red X + reason + "OK" button on failure
 * Background still runs the check-in API + updates the committee-wise
 * checked-in list below the scanner.
 */
export default function ScannerTab({ event }) {
  const [token, setToken] = useState("");
  const [modal, setModal] = useState(null); // { ok, message, allotment, committee }
  const [busy, setBusy] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [activeCam, setActiveCam] = useState("");
  // Prefer the rear/back camera on phones (facingMode:"environment").
  // On a laptop with no rear camera the browser just picks the only one.
  const [useRearCam, setUseRearCam] = useState(true);
  const [allotments, setAllotments] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  // "Scanner armed" indicator — true when the hidden capture input holds
  // focus. Flashes green on every captured keystroke so you can SEE the
  // scanner is being heard.
  const [scannerArmed, setScannerArmed] = useState(false);
  const [scannerFlash, setScannerFlash] = useState(false);
  const [phoneQrDataUrl, setPhoneQrDataUrl] = useState("");
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ token: "", ts: 0 });
  const barcodeBufRef = useRef({ buf: "", lastTs: 0 });
  const hiddenInputRef = useRef(null);
  const flashTimerRef = useRef(null);

  // -------- Load committees + allotments (for the checked-in list) ----
  const reload = async () => {
    setRefreshing(true);
    try {
      const [c, a] = await Promise.all([
        api.get(`/events/${event.event_id}/committees`),
        api.get(`/events/${event.event_id}/allotments`),
      ]);
      setCommittees(c.data || []);
      setAllotments(a.data || []);
    } catch {
      // silent — main scan flow is independent
    } finally { setRefreshing(false); }
  };
  useEffect(() => { reload(); }, [event.event_id]);

  // -------- Camera setup ----
  useEffect(() => {
    Html5Qrcode.getCameras().then((c) => {
      setCameras(c || []);
      // Pick the camera whose label hints at "back"/"rear"/"environment".
      // Falls back to the LAST camera (on most phones the rear cam is listed last).
      if (c?.length) {
        const rear = c.find((x) => /back|rear|environment/i.test(x.label || ""));
        setActiveCam((rear || c[c.length - 1]).id);
      }
    }).catch(() => {});
    return () => { stopCam(); };
  }, []);

  // "Open on phone" — show a QR code that encodes the current URL so the
  // operator can scan it with their phone's camera and continue scanning
  // delegate QRs on the phone (which has a proper rear camera).
  useEffect(() => {
    QRCode.toDataURL(window.location.href, { width: 220, margin: 1, color: { dark: "#0F172A", light: "#FFFFFF" } })
      .then(setPhoneQrDataUrl).catch(() => {});
  }, []);

  const submitToken = async (tk) => {
    const tok = (tk || "").trim();
    if (!tok) return;
    const now = Date.now();
    if (tok === lastScanRef.current.token && now - lastScanRef.current.ts < 3000) return;
    lastScanRef.current = { token: tok, ts: now };
    setBusy(true);
    try {
      const r = await api.post("/checkin/scan", { qr_token: tok, event_id: event.event_id });
      setModal({ ok: true, ...r.data });
      reload();
    } catch (e2) {
      const data = e2?.response?.data;
      if (data?.payment_pending) setModal({ ok: false, pending: true, ...data });
      else setModal({ ok: false, message: data?.message || "Scan failed", allotment: data?.allotment });
    } finally {
      setBusy(false);
    }
  };

  const markPaidAndCheckIn = async (allotmentId) => {
    try {
      const r = await api.post("/checkin/mark-paid", { allotment_id: allotmentId });
      setModal({ ok: true, ...r.data });
      toast.success("Marked paid & checked in");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as paid");
    }
  };

  const scan = (e) => { e?.preventDefault(); submitToken(token); setToken(""); };

  const startCam = async () => {
    try {
      const html5Qrcode = new Html5Qrcode("qr-reader");
      scannerRef.current = html5Qrcode;
      // When the user wants the rear camera, pass facingMode:"environment"
      // instead of a specific device id — this is the ONLY way iOS Safari
      // reliably picks the rear camera (device-id-based selection on iOS
      // sometimes mis-picks the FaceTime front cam). On desktops with a
      // single camera, this still just works.
      const camSource = useRearCam ? { facingMode: { exact: "environment" } } : (activeCam || { facingMode: "user" });
      try {
        await html5Qrcode.start(
          camSource,
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => submitToken(decodedText),
          () => {}
        );
      } catch (e1) {
        // Fallback for devices that don't support "exact" constraint.
        if (useRearCam) {
          await html5Qrcode.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 240, height: 240 } },
            (decodedText) => submitToken(decodedText),
            () => {}
          );
        } else {
          throw e1;
        }
      }
      setCamOn(true);
    } catch (e) {
      toast.error("Failed to start camera: " + (e?.message || e));
    }
  };

  const flipCam = async () => {
    if (camOn) await stopCam();
    setUseRearCam((v) => !v);
    // small delay so React updates `useRearCam` before startCam reads it
    setTimeout(() => startCam(), 100);
  };

  const stopCam = async () => {
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch { /* already stopped */ }
    scannerRef.current = null;
    setCamOn(false);
  };

  // -------- Hardware barcode scanner (keyboard-emulation) ----
  // A scanner types characters in a burst and (usually) ends with Enter or
  // Tab. Lessons from field reports ("beeps but nothing happens"):
  //  • Some scanners are configured with NO suffix — no Enter/Tab ever
  //    arrives, so a pure Enter-trigger never fires. We therefore
  //    AUTO-SUBMIT after 350ms of silence once the buffer is long enough.
  //  • Cheap scanners settle the USB link before the first char, so the
  //    inter-key gap window is generous (350ms).
  //  • Some driver/OS combos deliver keys with key="Unidentified" on
  //    keydown — we fall back to keypress charCodes for those.
  //  • The hidden capture input ALSO feeds the buffer via its onInput
  //    (value-based, timing-independent) — belt and braces.
  //  • A live "scanner heard" counter shows the last captured text so the
  //    operator can SEE whether keystrokes are arriving at all.
  const [lastCapture, setLastCapture] = useState("");
  const [liveBuf, setLiveBuf] = useState(""); // chars as they arrive — proves the page is hearing the scanner

  useEffect(() => {
    const isEditing = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toUpperCase();
      return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
    };

    const flush = () => {
      const buf = barcodeBufRef.current.buf.trim();
      setLiveBuf("");
      if (buf.length >= 5) {
        barcodeBufRef.current.buf = "";
        setLastCapture(buf.slice(0, 60));
        setScannerFlash(true);
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setScannerFlash(false), 400);
        submitToken(buf);
      } else {
        barcodeBufRef.current.buf = "";
      }
    };

    const armIdleFlush = () => {
      clearTimeout(barcodeBufRef.current.idleTimer);
      barcodeBufRef.current.idleTimer = setTimeout(() => {
        // Silence after a burst with no terminator → auto-submit.
        if (barcodeBufRef.current.buf.trim().length >= 5) flush();
        else { barcodeBufRef.current.buf = ""; setLiveBuf(""); }
      }, 350);
    };

    const ingestChar = (ch) => {
      const now = Date.now();
      const gap = now - barcodeBufRef.current.lastTs;
      if (gap > 350) barcodeBufRef.current.buf = "";
      barcodeBufRef.current.lastTs = now;
      barcodeBufRef.current.buf += ch;
      setLiveBuf(barcodeBufRef.current.buf.slice(-60));
      armIdleFlush();
    };

    let unidentifiedMode = false;

    const onKey = (e) => {
      const target = e.target;
      const isHidden = target?.dataset?.testid === "scanner-hidden-input";
      if (isEditing(target) && !isHidden) return;
      if (modal) return;

      if (e.key === "Enter" || e.key === "Tab") {
        if (barcodeBufRef.current.buf.trim().length >= 5) {
          e.preventDefault();
          clearTimeout(barcodeBufRef.current.idleTimer);
          flush();
        }
        return;
      }
      // When the scanner is typing into the hidden capture input, its
      // onInput handler ingests from the VALUE — ingesting here too would
      // double every character (we saw "NNOOSSUUFF..." in testing).
      if (isHidden) return;
      if (e.key && e.key.length === 1) {
        unidentifiedMode = false;
        ingestChar(e.key);
      } else if (e.key === "Unidentified") {
        // keydown gave us nothing usable — keypress fallback takes over.
        unidentifiedMode = true;
      }
    };

    const onKeypress = (e) => {
      if (!unidentifiedMode) return;
      const target = e.target;
      const isHidden = target?.dataset?.testid === "scanner-hidden-input";
      if (isEditing(target) && !isHidden) return;
      if (modal) return;
      if (e.charCode && e.charCode >= 32) ingestChar(String.fromCharCode(e.charCode));
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("keypress", onKeypress);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keypress", onKeypress);
      clearTimeout(flashTimerRef.current);
      clearTimeout(barcodeBufRef.current.idleTimer);
    };
  }, [modal]);

  // Hidden input also feeds the buffer directly from its VALUE — covers
  // the case where the scanner types into it as ordinary text input.
  const onHiddenInput = (e) => {
    if (modal) return;
    const v = e.target.value || "";
    if (!v) return;
    for (const ch of v) {
      barcodeBufRef.current.lastTs = Date.now();
      barcodeBufRef.current.buf += ch;
    }
    setLiveBuf(barcodeBufRef.current.buf.slice(-60));
    e.target.value = "";
    clearTimeout(barcodeBufRef.current.idleTimer);
    barcodeBufRef.current.idleTimer = setTimeout(() => {
      const buf = barcodeBufRef.current.buf.trim();
      setLiveBuf("");
      if (buf.length >= 5) {
        barcodeBufRef.current.buf = "";
        setLastCapture(buf.slice(0, 60));
        setScannerFlash(true);
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setScannerFlash(false), 400);
        submitToken(buf);
      } else {
        barcodeBufRef.current.buf = "";
      }
    }, 350);
  };

  // "Arm scanner" — force-focus the hidden input so stray clicks on the
  // page don't blur it. Operators tap this if the "armed" pill goes
  // grey (e.g. the browser stole focus with a notification).
  const armScanner = () => { hiddenInputRef.current?.focus(); };

  // -------- Derived: checked-in allotments grouped by committee ----
  const groupedCheckedIn = useMemo(() => {
    const map = new Map();
    for (const c of committees) map.set(c.committee_id, { committee: c, rows: [] });
    for (const a of allotments) {
      if (!a.checked_in) continue;
      const g = map.get(a.committee_id);
      if (g) g.rows.push(a);
      else map.set(a.committee_id, { committee: { committee_id: a.committee_id, name: "Unassigned" }, rows: [a] });
    }
    // Newest check-in first within each committee
    for (const g of map.values()) g.rows.sort((x, y) => (y.checked_in_at || "").localeCompare(x.checked_in_at || ""));
    return Array.from(map.values());
  }, [committees, allotments]);

  const totalCheckedIn = useMemo(() => allotments.filter((a) => a.checked_in).length, [allotments]);
  const totalAllotments = allotments.length;
  const dismissModal = () => { setModal(null); hiddenInputRef.current?.focus(); };

  // Undo a check-in straight from the checked-in list.
  const reverseCheckin = async (a) => {
    if (!window.confirm(`Undo check-in for ${a.student_name}?`)) return;
    try {
      await api.post(`/allotments/${a.allotment_id}/reverse-checkin`, {});
      toast.success("Check-in reversed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reverse check-in");
    }
  };

  return (
    <div>
      {/* Hidden input that captures barcode-scanner keystrokes when nothing
          else is focused. We don't actually read its value here (the
          window keydown handler does), but keeping it focused stops the
          browser focus from drifting to weird places between scans. */}
      <input
        ref={hiddenInputRef}
        type="text"
        autoFocus
        className="sr-only"
        data-testid="scanner-hidden-input"
        onChange={onHiddenInput}
        onFocus={() => setScannerArmed(true)}
        onBlur={(e) => {
          setScannerArmed(false);
          if (!modal) setTimeout(() => e.target.focus(), 50);
        }}
        aria-hidden="true"
      />

      <div className="dashboard-card p-6 mb-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center"><QrCode className="w-6 h-6 text-orange-600" /></div>
            <div>
              <h3 className="font-display font-bold text-xl">Check-in Scanner</h3>
              <p className="text-sm text-slate-500">Camera, USB / wireless barcode scanner, or paste a QR token.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={armScanner}
              title={scannerArmed ? "Scanner is armed — just scan the QR" : "Click to re-arm the barcode scanner"}
              data-testid="arm-scanner-btn"
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${scannerFlash ? "bg-emerald-500 text-white border-emerald-500 animate-pulse" : scannerArmed ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"}`}
            >
              <Keyboard className="w-3.5 h-3.5" />
              {scannerFlash ? "Scan received!" : scannerArmed ? "Barcode scanner armed" : "Scanner idle — click to arm"}
              <span className={`w-1.5 h-1.5 rounded-full ${scannerArmed ? "bg-emerald-400 animate-pulse" : "bg-slate-400"}`} />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 mt-4">
          {/* Camera */}
          <div className="dashboard-card p-4 bg-slate-50">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="font-bold text-sm flex items-center gap-2"><Camera className="w-4 h-4" /> Camera</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (camOn) { flipCam(); } else { setUseRearCam((v) => !v); }
                  }}
                  className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border font-semibold ${useRearCam ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}
                  data-testid="cam-facing-toggle"
                  title="Switch between the front (selfie) and rear (back) camera. Rear is best for scanning delegate badges."
                >
                  <SwitchCamera className="w-3.5 h-3.5" /> {useRearCam ? "Rear camera" : "Front camera"}
                </button>
                {!camOn ? (
                  <button onClick={startCam} className="btn-primary py-2 text-xs" data-testid="cam-start"><Power className="w-3 h-3" /> Start</button>
                ) : (
                  <button onClick={stopCam} className="btn-ghost py-2 text-xs" data-testid="cam-stop"><PowerOff className="w-3 h-3" /> Stop</button>
                )}
              </div>
            </div>
            {cameras.length > 1 && !useRearCam && (
              <select value={activeCam} onChange={(e) => setActiveCam(e.target.value)} className="input-pill text-xs py-2 mb-2" disabled={camOn}>
                {cameras.map((c) => <option key={c.id} value={c.id}>{c.label || c.id}</option>)}
              </select>
            )}
            <div id="qr-reader" className={`rounded-xl overflow-hidden ${camOn ? "border-2 border-orange-300" : ""}`} style={{ width: "100%", minHeight: camOn ? 320 : 0 }} />
            {!camOn && <p className="text-xs text-slate-400 mt-2">Click <b>Start</b> to scan QR codes live. On mobile phones the <b>Rear camera</b> is auto-picked.</p>}

            {/* Phone-sync QR — scan this with your phone to open the same scanner page on it */}
            {phoneQrDataUrl && (
              <details className="mt-3 rounded-xl border border-slate-200 bg-white" data-testid="phone-sync-details">
                <summary className="cursor-pointer select-none px-3 py-2 text-xs font-bold flex items-center gap-1.5 text-slate-700">
                  <Smartphone className="w-3.5 h-3.5" /> Use my phone to scan
                </summary>
                <div className="px-3 pb-3 pt-1 flex items-center gap-3">
                  <img src={phoneQrDataUrl} alt="Open scanner on phone" className="w-28 h-28 rounded-lg border border-slate-100" data-testid="phone-sync-qr" />
                  <div className="text-xs text-slate-600 leading-relaxed">
                    <b>Point your phone's camera at this QR</b> and tap the link. The scanner page opens on your phone — tap <b>Start</b> and your phone's rear camera becomes the scanner. Both scanners stay in sync automatically.
                  </div>
                </div>
              </details>
            )}
          </div>

          {/* Manual + barcode-scanner */}
          <div className="dashboard-card p-4 bg-slate-50">
            <div className="font-bold text-sm mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> Manual entry / Barcode scanner</div>
            <form onSubmit={scan}>
              <input placeholder="Paste QR token (or just point your barcode scanner anywhere on this page)" className="input-pill" value={token} onChange={(e) => setToken(e.target.value)} data-testid="scanner-input" />
              <button type="submit" disabled={busy} className="btn-primary mt-3 w-full py-2.5 text-sm" data-testid="scanner-submit">{busy ? "Checking…" : "Verify"}</button>
            </form>
            <p className="text-xs text-slate-400 mt-3">
              Wired or wireless barcode scanners in keyboard mode work everywhere on this page — no focus needed. Just scan the QR and the popup confirms instantly.
            </p>
            {/* Live diagnostic — shows keystrokes AS they arrive. If a scan
                beeps but NOTHING appears here, the scanner is not in
                USB-keyboard (HID) mode — scan its "USB HID Keyboard" config
                barcode from the scanner's manual. */}
            <div className="mt-2 text-[11px] bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-600 font-mono break-all min-h-[28px]" data-testid="scanner-live-monitor">
              {liveBuf
                ? <span>Hearing: <span className="font-bold text-emerald-700">{liveBuf}</span></span>
                : lastCapture
                  ? <span>Last heard: <span className="font-bold text-slate-800">{lastCapture}</span></span>
                  : <span className="text-slate-400">Scanner monitor: scan a QR — the decoded text should appear here instantly.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Checked-in list panel — event-scoped, committee-wise */}
      <div className="dashboard-card p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Users className="w-5 h-5" /></div>
            <div>
              <h4 className="font-display font-bold text-lg">Checked in so far</h4>
              <p className="text-xs text-slate-500">Live list for <b>{event.name}</b> only — refreshes after every scan.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="checkedin-count-wrap">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-1.5 text-xs flex items-center gap-2">
                <span className="font-display font-black text-2xl text-emerald-700" data-testid="checked-in-total">{totalCheckedIn}</span>
                <span className="text-emerald-800/80">/ {totalAllotments} delegates</span>
              </div>
            </div>
            <button onClick={reload} disabled={refreshing} className="btn-ghost text-xs py-1.5 px-2 disabled:opacity-50" title="Refresh"><RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /></button>
          </div>
        </div>

        {totalCheckedIn === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">No check-ins yet — start scanning to populate this list.</div>
        ) : (
          <div className="space-y-3">
            {groupedCheckedIn.filter((g) => g.rows.length > 0).map((g) => (
              <details key={g.committee.committee_id} open className="rounded-xl border border-slate-100" data-testid={`checkedin-com-${g.committee.committee_id}`}>
                <summary className="cursor-pointer select-none px-4 py-2.5 bg-slate-50/50 rounded-t-xl flex items-center justify-between font-bold text-sm">
                  <span>{g.committee.name}</span>
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">{g.rows.length} checked in</span>
                </summary>
                <div className="divide-y divide-slate-50">
                  {g.rows.map((a) => (
                    <div key={a.allotment_id} className="px-4 py-2 flex items-center gap-3 text-sm" data-testid={`checkedin-row-${a.allotment_id}`}>
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{a.student_name}</div>
                        <div className="text-xs text-slate-500 truncate">{a.portfolio} · {a.school_name}</div>
                      </div>
                      <button
                        onClick={() => reverseCheckin(a)}
                        title={`Undo check-in for ${a.student_name}`}
                        data-testid={`reverse-${a.allotment_id}`}
                        className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 hover:bg-rose-100 hover:text-rose-700 transition-colors cursor-pointer shrink-0"
                      >
                        ✓ Checked in · undo
                      </button>
                      <div className="text-xs text-slate-400 shrink-0">{a.checked_in_at ? new Date(a.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                      {a.paid ? <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 rounded-full px-1.5 py-0.5">Paid</span>
                              : <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 rounded-full px-1.5 py-0.5">Unpaid</span>}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      {/* Scan-result popup */}
      {modal && <ScanResultModal modal={modal} onClose={dismissModal} committees={committees} onMarkPaid={markPaidAndCheckIn} />}
    </div>
  );
}

const MODAL_TONE = {
  ok: { border: "border-emerald-500", bg: "bg-gradient-to-br from-emerald-50 to-green-100/70", dot: "bg-emerald-500", title: "text-emerald-700", sub: "text-emerald-700/80", btn: "bg-emerald-600 hover:bg-emerald-700", heading: "Check-in Successful", testid: "scan-success-modal" },
  pending: { border: "border-amber-400", bg: "bg-gradient-to-br from-amber-50 to-yellow-100/80", dot: "bg-amber-400", title: "text-amber-700", sub: "text-amber-700/80", btn: "bg-slate-700 hover:bg-slate-800", heading: "Payment Pending", testid: "scan-pending-modal" },
  fail: { border: "border-rose-500", bg: "bg-gradient-to-br from-rose-50 to-red-100/70", dot: "bg-rose-500", title: "text-rose-700", sub: "text-rose-700/80", btn: "bg-rose-600 hover:bg-rose-700", heading: "Check-in Failed", testid: "scan-failure-modal" },
};

function ScanResultModal({ modal, onClose, committees, onMarkPaid }) {
  const ok = !!modal.ok;
  const pending = !!modal.pending;
  const tone = MODAL_TONE[ok ? "ok" : pending ? "pending" : "fail"];
  const a = modal.allotment || {};
  const committee = modal.committee || committees.find((c) => c.committee_id === a.committee_id);
  const [marking, setMarking] = useState(false);

  // Auto-focus the dismiss button so a scanner's trailing Enter never marks anyone paid by accident.
  const okBtnRef = useRef(null);
  useEffect(() => { okBtnRef.current?.focus(); }, [ok, pending]);

  const markPaid = async () => {
    if (!window.confirm(`Confirm ${a.student_name} has paid? They will be marked PAID and checked in.`)) return;
    setMarking(true);
    await onMarkPaid(a.allotment_id);
    setMarking(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border-2 ${tone.border}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={tone.testid}
      >
        <div className={`p-6 text-center ${tone.bg}`}>
          <div className={`w-20 h-20 rounded-full ${tone.dot} flex items-center justify-center mx-auto mb-3 shadow-lg animate-pop`}>
            {ok ? <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.5} /> : pending ? <Clock className="w-12 h-12 text-white" strokeWidth={2.5} /> : <XCircle className="w-12 h-12 text-white" strokeWidth={2.5} />}
          </div>
          <h2 className={`font-display font-black text-2xl ${tone.title}`} data-testid="scan-modal-title">{tone.heading}</h2>
          <p className={`text-sm mt-1 ${tone.sub}`}>{pending ? "This delegate is not marked paid. Collect / verify payment before letting them in." : modal.message || (ok ? "Delegate marked present." : "Could not verify this QR.")}</p>
        </div>

        {a.student_name && (
          <div className="p-5 space-y-2.5">
            <Detail label="Delegate" value={a.student_name} />
            {a.student_email && <Detail label="Email" value={a.student_email} small />}
            {a.school_name && <Detail label="School" value={a.school_name} />}
            <Detail label="Committee" value={committee?.name || "—"} />
            <Detail label="Portfolio" value={a.portfolio || "—"} />
            <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
              <span className="text-slate-500">Payment</span>
              <span className={`font-bold px-2 py-0.5 rounded-full text-xs ${a.paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {a.paid ? "✓ Paid" : "⏳ Unpaid"}
              </span>
            </div>
          </div>
        )}

        <div className="p-4 bg-slate-50 border-t border-slate-100 space-y-2">
          {pending && (
            <button
              onClick={markPaid}
              disabled={marking}
              className="w-full py-3 rounded-2xl font-bold text-amber-950 bg-amber-400 hover:bg-amber-500 shadow-lg disabled:opacity-60"
              data-testid="scan-modal-mark-paid"
            >
              {marking ? "Marking…" : "Mark as Paid & Check in"}
            </button>
          )}
          <button
            ref={okBtnRef}
            onClick={onClose}
            autoFocus
            className={`w-full py-3 rounded-2xl font-bold text-white shadow-lg ${tone.btn}`}
            data-testid="scan-modal-ok"
          >
            {pending ? "Close — not checked in" : "OK — Scan next"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, small }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className={`font-semibold text-right ${small ? "text-xs text-slate-600 break-all" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}
